import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { Subject, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { NoteService } from 'src/app/service/data/note.service'
import { TagService } from 'src/app/service/data/tag.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { DdcButtonAiComponent } from 'src/app/shared/ddc/ddc-button-ai/ddc-button-ai.component'
import { DdcButtonSOneComponent } from 'src/app/shared/ddc/ddc-button-s-one/ddc-button-s-one.component'
import { DdcButtonUOneComponent } from 'src/app/shared/ddc/ddc-button-u-one/ddc-button-u-one.component'
import { DdcElemChooserOneComponent } from 'src/app/shared/ddc/ddc-elem-chooser-one/ddc-elem-chooser-one.component'
import { DdcTextGradientComponent } from 'src/app/shared/ddc/ddc-text-gradient/ddc-text-gradient.component'
import {
  Article,
  ArticlesByLanguage,
  Languages,
  PartOfSpeech,
  Themes,
} from 'src/app/shared/enums/enums'
import { SelectOption } from 'src/app/shared/interfaces/word'
import {
  CreateWordPairDicArgs,
  CreateWordPairDicCommand,
} from '../commands/create-word-pair-dic/create-word-pair-dic.command'
import { DdcAddDialogThreeComponent } from '../ddc-add-dialog-three/ddc-add-dialog-three.component'
@Component({
  selector: 'ddc-add-dialog',
  templateUrl: './ddc-add-dialog.component.html',
  styleUrls: ['./ddc-add-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    DdcTextGradientComponent,
    DdcButtonUOneComponent,
    FormsModule,
    DdcElemChooserOneComponent,
    DdcButtonSOneComponent,
    DdcButtonAiComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
})
export class DdcAddDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()
  protected CreateWordPairDicCommand: CreateWordPairDicCommand

  @Input() isMobileSheet: boolean = false
  @Output() closeSheet = new EventEmitter<any>()

  // Initialize maxlength properties first
  _privacyMaxlength: number = 4
  _wordMaxlength: number = 100
  _exampleMaxlength: number = 5000
  _definitionMaxlength: number = 10000
  _noteTitleMaxlength: number = 255
  _noteTextMaxlength: number = 300000
  _posMaxlength: number = 3
  _articleMaxlength: number = 3

  _form = new FormGroup({
    // ! Word 1
    _1_language: new FormControl('', [Validators.required]),
    _1_article: new FormControl(null, []),
    _1_word: new FormControl('', [Validators.required, Validators.maxLength(this._wordMaxlength)]),
    _1_pos: new FormControl(null, []),
    _1_example: new FormControl(null, [Validators.maxLength(this._exampleMaxlength)]),
    _1_definition: new FormControl(null, [Validators.maxLength(this._definitionMaxlength)]),

    // ! Word 2 (optional for pair)
    _2_language: new FormControl('', [Validators.required]),
    _2_article: new FormControl(null, []),
    _2_word: new FormControl('', [Validators.required, Validators.maxLength(this._wordMaxlength)]),
    _2_pos: new FormControl(null, []),
    _2_example: new FormControl(null, [
      Validators.minLength(1),
      Validators.maxLength(this._exampleMaxlength),
    ]),
    _2_definition: new FormControl(null, [
      Validators.minLength(1),
      Validators.maxLength(this._definitionMaxlength),
    ]),

    // ! privacy
    _privacy: new FormControl('P01', [
      Validators.required,
      Validators.maxLength(this._privacyMaxlength),
    ]),

    // ! dictionary
    _dictionary_title: new FormControl(null, [Validators.maxLength(100), Validators.minLength(1)]),
    _dictionary_description: new FormControl(null, [
      Validators.maxLength(1000),
      Validators.minLength(1),
    ]),

    // ! note
    _note_title: new FormControl(null, [Validators.maxLength(255), Validators.minLength(1)]),
    _note_text: new FormControl(null, [Validators.maxLength(300000)]),
  })

  // Form-related signals
  private formValues = signal(this._form.value)
  private selectedTags = signal<{ id: number; label: string }[]>([])
  private selectedTags2 = signal<{ id: number; label: string }[]>([])
  private selectedCategory = signal<{ id: number; label: string }[]>([])
  private selectedCategory2 = signal<{ id: number; label: string }[]>([])
  private selectedDics = signal<{ id: number; label: string }[]>([])

  // Computed signal for command args
  private commandArgs = computed(() => {
    const formValue = this.formValues()
    const args: CreateWordPairDicArgs = {
      word1: {
        language: formValue._1_language,
        article: formValue._1_article,
        word: formValue._1_word,
        pos: formValue._1_pos,
        definition: formValue._1_definition,
        example: formValue._1_example,
        category: this.selectedCategory()?.length > 0 ? this.selectedCategory()[0] : undefined,
        tags: this.selectedTags(),
      },
      privacy: formValue._privacy,
      dictionaries: this.selectedDics(),
    }

    if (this._selectedChipOptions.includes('pair')) {
      args.word2 = {
        language: formValue._2_language,
        article: formValue._2_article,
        word: formValue._2_word,
        pos: formValue._2_pos,
        definition: formValue._2_definition,
        example: formValue._2_example,
        category: this.selectedCategory2()?.length > 0 ? this.selectedCategory2()[0] : undefined,
        tags: this.selectedTags2(),
      }
    }

    if (this._selectedChipOptions.includes('dictionary')) {
      args.dictionary_title = formValue._dictionary_title
      args.dictionary_description = formValue._dictionary_description
    }

    return args
  })

  _isDialogLoading: boolean = true
  _isSubscriptionError: boolean = false
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string
  _addedField: string = ''
  _addedField2: string = ''
  private themeSubscription: Subscription

  _isCreateBtnLoading = false
  _submitBtnText = 'Create'

  _addError: string = ''

  _selectedChipOptions: string[] = []
  _selectedPrivacyChipOptions: string = ''

  _userTags: any[] = []
  _userCats: any[] = []
  _userDics: any[] = []

  _loadedCats: boolean = false
  _loadedTags: boolean = false
  _loadedDics: boolean = false

  _selectedTags: { id: number; label: string }[] = []
  _selectedTags2: { id: number; label: string }[] = []
  _selectedDics: { id: number; label: string }[] = []
  _selectedArticle: string = null
  _selectedArticle2: string = null
  _selectedPos: string = null
  _selectedPos2: string = null
  _selectedLanguage: string
  _selectedLanguage2: string
  _selectedCategory: { id: number; label: string }[] = []
  _selectedCategory2: { id: number; label: string }[] = []

  languages: SelectOption[] = []
  languages2: SelectOption[] = []
  articles: SelectOption[] = []
  articles2: SelectOption[] = []
  poses: SelectOption[] = []
  poses2: SelectOption[] = []

  _createdWordContext: any
  _createdPairContext: any
  _targetContentId: number

  _selectedLngHasArticle: boolean = false
  _selectedLngHasArticle2: boolean = false

  _wordCreated: boolean = false
  _pairCreated: boolean = false
  _noteCreated: boolean = false
  _isNoteCreateLoading: boolean = false

  _hasDic: boolean = false
  _targetDicId: number = 0
  _targetDicTitle: string = ''

  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick-5.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  partOfSpeeches: any

  constructor(
    private logger: DdcLoggerService,
    public dialog: MatDialog,
    public themeService: ThemeService,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private dic_service: DictionaryService,
    private note_service: NoteService,
    @Optional() private dialogRef: MatDialogRef<DdcAddDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    createWordPairDicCmd: CreateWordPairDicCommand,
    private formBuilder: FormBuilder,
  ) {
    this.CreateWordPairDicCommand = createWordPairDicCmd
    if (!this.data) {
      this.data = { targetChip: 'word', hasDic: false, dicId: 0, dicTitle: '' }
    }
  }

  ngOnInit(): void {
    this.handleDefaultChip(this.data.targetChip)
    if (this.data.hasDic) {
      this._hasDic = true
      this._targetDicId = this.data.dicId
      this._targetDicTitle = this.data.dicTitle
      this._submitBtnText = 'Add To Dictionary'
    }

    this._selectedPrivacyChipOptions = 'P01'

    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    // Setup form value changes listener
    this._form.valueChanges.subscribe(value => {
      this.formValues.set(value)
      this.updateCommandArgsFromSignals()
    })

    //set selectedDics from data if available
    if (this.data.hasDic && this.data.dicId && this.data.dicTitle.length > 0) {
      this.selectedDics.set([{ id: this.data.dicId, label: this.data.dicTitle }])
    }

    Promise.all([
      this.getLanguages(),
      this.getLanguages2(),
      this.getArticles(),
      this.getArticles2(),
      this.getPos(),
      this.getPos2(),
      this.getUserTags(),
      this.getUserCats(),
      this.getUserDics(),
    ]).then(() => {
      setTimeout(() => {
        this._isDialogLoading = false
      }, 500)
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  handleDefaultChip(chip: string): void {
    this.logger.info('DdcAddDialogComponent | handleDefaultChip | chip: ' + chip)
    this._selectedChipOptions = [chip]
  }

  isFormValid(): boolean {
    if (this._selectedChipOptions.includes('word')) {
      const lng = this._form.get('_1_language').valid
      const art = this._form.get('_1_article').valid
      const word = this._form.get('_1_word').valid
      const pos = this._form.get('_1_pos').valid
      const exp = this._form.get('_1_example').valid
      const def = this._form.get('_1_definition').valid

      if (lng && art && word && pos && exp && def) return true
    } else if (this._selectedChipOptions.includes('pair')) {
      if (this._form.valid) return true
    } else if (this._selectedChipOptions.includes('dictionary')) {
      const dic_title = this._form.get('_dictionary_title')
      const dic_description = this._form.get('_dictionary_description')
      if (
        dic_title &&
        dic_description &&
        dic_title.valid &&
        dic_description.valid &&
        dic_title.value &&
        dic_description.value &&
        dic_title.value.length > 0 &&
        dic_description.value.length > 0
      )
        return true
    } else if (this._selectedChipOptions.includes('note')) {
      const note_title = this._form.get('_note_title')
      if (note_title && note_title.valid && note_title.value && note_title.value.length > 0)
        return true
    }
    return false
  }

  createNote(): void {
    this.logger.info('DdcAddDialogComponent | createNote')
    const title = this._form.get('_note_title')?.value
    const text = this._form.get('_note_text')?.value
    const privacy = this._selectedPrivacyChipOptions || 'P01'

    if (!title || title.length === 0) return

    this._isNoteCreateLoading = true
    this._addError = ''

    this.note_service.add_note(title, text, privacy).subscribe((res: any) => {
      this._isNoteCreateLoading = false
      if (res.success) {
        this._noteCreated = true
        this._addedField = title
      } else {
        this._addError = typeof res.res === 'string' ? res.res : 'Failed to create note.'
        if (res.res?.detail) this._addError = res.res.detail
        if (res.res?.title) this._addError = res.res.title
        if (
          this._addError.toLowerCase().includes('subscription') ||
          this._addError.toLowerCase().includes('limit')
        ) {
          this._isSubscriptionError = true
        }
      }
    })
  }

  getPos(): void {
    this.logger.info('DdcAddDialogComponent | getPos')

    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getPos2(): void {
    this.logger.info('DdcAddDialogComponent | getPos2')

    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses2.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  updateSelectedTags(tags: any): void {
    this.logger.info('DdcAddDialogComponent | updateSelectedTags | tags:', tags)
    this.selectedTags.set(tags)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedTags2(tags: any): void {
    this.logger.info('DdcAddDialogComponent | updateSelectedTags2 | tags:', tags)
    this.selectedTags2.set(tags)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedCat(cat: any): void {
    this.logger.info('DdcAddDialogComponent | updateSelectedCat: cat: ' + cat)
    this.selectedCategory.set(cat)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedCat2(cat: any): void {
    this.logger.info('DdcAddDialogComponent | updateSelectedCat2: cat: ' + cat)
    this.selectedCategory2.set(cat)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedDics(dictionaries: any): void {
    this.logger.info('updateSelectedDics: dictionaries: ' + dictionaries)
    this.selectedDics.set(dictionaries)
    this.updateCommandArgsFromSignals()
  }

  private updateCommandArgsFromSignals(): void {
    this.logger.info(
      'DdcAddDialogComponent | updateCommandArgs | Current args:',
      this.commandArgs(),
    )
    this.CreateWordPairDicCommand.setArgs(this.commandArgs())
  }

  getLanguages(): void {
    this.logger.info('DdcAddDialogComponent | getLanguages')

    this.languages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }

  getLanguages2(): void {
    this.logger.info('DdcAddDialogComponent | getLanguages2')

    this.languages2 = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }

  onLanguageChange(event: any): void {
    this.logger.info('DdcAddDialogComponent | onLanguageChange: event: ' + event)

    const selectedLng = event.value

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray != undefined) {
      this._selectedLngHasArticle = true
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }

    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  onLanguageChange2(event: any): void {
    this.logger.info('DdcAddDialogComponent | onLanguageChange2: event: ' + event)

    const selectedLng2 = event.value

    this.articles2 = []
    this.articles2.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray2 = ArticlesByLanguage[selectedLng2]

    if (articlesArray2 != undefined) {
      this._selectedLngHasArticle2 = true
    } else {
      this._selectedLngHasArticle2 = false
    }

    for (const value of articlesArray2) {
      this.articles2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getArticles(): void {
    this.logger.info('DdcAddDialogComponent | getArticles')

    const articlesArray = Object.values(Article) as string[]

    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getArticles2(): void {
    this.logger.info('DdcAddDialogComponent | getArticles2')

    const articlesArray2 = Object.values(Article) as string[]

    this.articles2.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray2) {
      this.articles2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getUserTags(): void {
    this.logger.info('DdcAddDialogComponent | getUserTags')

    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._userTags = res
        this._loadedTags = true
      }
    })
  }

  getUserCats(): void {
    this.logger.info('DdcAddDialogComponent | getUserCats')

    this.cat_service.get_cats().subscribe((res: any) => {
      if (res) {
        this._userCats = res
        this._loadedCats = true
      }
    })
  }

  getUserDics(): void {
    this.dic_service.get_dics(true).subscribe({
      next: (res: any) => {
        if (res) {
          // Map the response to transform 'title' to 'label'
          this._userDics = res.map((dic: { id: number; title: string }) => ({
            id: dic.id,
            label: dic.title,
          }))
          this._loadedDics = true
        } else {
        }
      },
      error: (error: any) => {
        this._isSubscriptionError = true
      },
    })
  }

  closeDialog(): void {
    var closeMsg = 'closed'
    if (this._hasDic && this._selectedChipOptions.includes('word')) closeMsg = 'refreshDicWords'
    else if (this._hasDic && this._selectedChipOptions.includes('pair'))
      closeMsg = 'refreshDicPairs'

    if (this.isMobileSheet) {
      if (this._wordCreated || this._pairCreated || this._noteCreated) {
        if (this._wordCreated && this._createdWordContext) {
          this.closeSheet.emit({ type: 'word', element: this._createdWordContext })
        } else if (this._pairCreated && this._createdPairContext) {
          this.closeSheet.emit({ type: 'pair', element: this._createdPairContext })
        } else if (this._noteCreated) {
          this.closeSheet.emit({ type: 'note' })
        }
      } else {
        this.closeSheet.emit(closeMsg)
      }
    } else if (this._wordCreated || this._pairCreated || this._noteCreated) {
      if (this._wordCreated && this._createdWordContext) {
        this.dialogRef.close({ type: 'word', element: this._createdWordContext })
      } else if (this._pairCreated && this._createdPairContext) {
        this.dialogRef.close({ type: 'pair', element: this._createdPairContext })
      } else if (this._noteCreated) {
        this.dialogRef.close({ type: 'note' })
      }
    } else {
      this.dialogRef.close(closeMsg)
    }
  }

  openAddDictionaryDialog(): void {
    this.logger.info('DdcAddDialogComponent | openAddDictionaryDialog')
    const dialogRef = this.dialog.open(DdcAddDialogThreeComponent, {
      data: 'dictionary',
    })

    dialogRef.afterClosed().subscribe(result => {
      // Handle any actions after the dialog is closed, e.g., updating the user's profile picture.
      if (result === 'closeAll') {
        if (this.isMobileSheet) {
          this.closeSheet.emit('closeAll')
        } else {
          this.dialogRef.close()
        }
      }
    })
  }

  get w1(): any {
    return this._form.get('_1_word')
  }
  get w2(): any {
    return this._form.get('_2_word')
  }

  get lng1(): any {
    return this._form.get('_1_language')
  }
  get lng2(): any {
    return this._form.get('_2_language')
  }

  get desc1(): any {
    return this._form.get('_1_definition')
  }
  get desc2(): any {
    return this._form.get('_2_definition')
  }

  get exp1(): any {
    return this._form.get('_1_example')
  }
  get exp2(): any {
    return this._form.get('_2_example')
  }
}
